import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    GlobalStyles,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import AttachMoney from '@mui/icons-material/AttachMoney';
import Percent from '@mui/icons-material/Percent';
import { formatCurrency } from '../../common/formatters';
import { POS_GRADIENT } from '../../theme/tokens';
import { KitButton, KitCard, KitItem, KitList, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitBanner,
    KitChip,
    KitChips,
    KitField,
    KitInput,
    KitKV,
    KitSearch,
    KitSelect,
    KitStepper,
    KitStickyToolbar,
    KitTextarea,
    KitToolbarButton
} from '../common/MobileKitExtras';
import { useInterFont, useLockZoom } from '../layout/MobileLayout';

/**
 * Tela de venda (PDV) no shell mobile — reproduz os protótipos
 * `prototipos-mobile/vendas/pdv.html` (grade de produtos, folhas "Adicionar à venda",
 * "Carrinho" e "Cliente") e `vendas/pdv-pagamento.html` (tela "Pagamento" e folha
 * "Venda concluída!"). Barra superior e rodapé usam o degradê do PDV (`POS_GRADIENT`).
 *
 * Só é montada por `pages/Sale.js` quando `useIsMobileShell()` é verdadeiro.
 * Não tem regra de negócio própria: todos os handlers, estados e cálculos
 * vêm de `Sale.js` por props — aqui é só apresentação e fluxo mobile.
 */

const BackIcon = protoIcon('back');
const SearchIcon = protoIcon('search');
const ScanIcon = protoIcon('scan');
const UsersIcon = protoIcon('users');
const DotsIcon = protoIcon('dots');
const PackageIcon = protoIcon('package');
const CartIcon = protoIcon('cart');
const CheckIcon = protoIcon('check');
const PlusIcon = protoIcon('plus');
const PrintIcon = protoIcon('print');
const PercentIcon = protoIcon('percent');
const DollarIcon = protoIcon('dollar');
const ZapIcon = protoIcon('zap');
const CardIcon = protoIcon('card');
const TrashIcon = protoIcon('trash');
const AlertIcon = protoIcon('alert');
const WalletIcon = protoIcon('wallet');
const MoonIcon = protoIcon('moon');
const SunIcon = protoIcon('sun');
const BanIcon = protoIcon('ban');
const TagIcon = protoIcon('tag');
const EditIcon = protoIcon('edit');
const XIcon = protoIcon('x');
const RightIcon = protoIcon('right');

const APP_BAR_HEIGHT = 56;
const WARN_LIGHT = '#b45309';
const PRODUCTS_PAGE = 60;
const CUSTOMERS_LIMIT = 50;

const parseMoney = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return value;
    const n = Number(String(value).trim().replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
};

const formatMoney = (n) => Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const hasMoney = (value) => parseMoney(value) > 0;

// `--pos` do protótipo (claro #667eea→#764ba2, escuro #1e293b→#334155)
const posGradient = (mode) => `linear-gradient(135deg, ${POS_GRADIENT.pos1[mode]}, ${POS_GRADIENT.pos2[mode]})`;

function initialsOf(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : parts[0][1] || '')).toUpperCase();
}

/* ------------------------------------------------------------------ */
/* Peças de layout                                                     */
/* ------------------------------------------------------------------ */

// Container de tela cheia (100dvh): barra superior + corpo rolável + rodapé fixo (`.screen`).
function ScreenShell({ children }) {
    useInterFont();
    return (
        <Box
            data-mobile-shell
            sx={{
                position: 'relative',
                height: '100vh',
                '@supports (height: 100dvh)': { height: '100dvh' },
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxSizing: 'border-box',
                backgroundColor: 'background.default',
                color: 'text.primary',
                fontSize: 14,
                lineHeight: 1.4
            }}
        >
            <GlobalStyles
                styles={{
                    'html, body': { overflowX: 'hidden', WebkitTextSizeAdjust: '100%', textSizeAdjust: '100%', touchAction: 'pan-x pan-y' },
                    '*': { scrollbarWidth: 'none', msOverflowStyle: 'none' },
                    '*::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
                    '.MuiButton-root': { minHeight: 44 },
                    '.MuiInputBase-input, .MuiSelect-select': { fontSize: '16px !important' }
                }}
            />
            {children}
        </Box>
    );
}

// `.icon-btn` dentro da barra do PDV (`.appbar.pos .icon-btn`): 40x40, raio 12, ícone branco.
const appBarButtonSx = { width: 40, height: 40, minWidth: 40, minHeight: 40, borderRadius: '12px', color: '#fff', flexShrink: 0, p: 0 };

function BarButton({ label, onClick, children }) {
    return (
        <IconButton aria-label={label} onClick={onClick} sx={appBarButtonSx}>
            {children}
        </IconButton>
    );
}

// `.appbar.pos`: degradê do PDV, voltar + título (17/800) + subtítulo + ações.
function PosAppBar({ title, sub, onBack, backLabel = 'Voltar', actions }) {
    return (
        <Box
            component="header"
            sx={(theme) => ({
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxSizing: 'border-box',
                p: 'calc(10px + env(safe-area-inset-top, 0px)) calc(12px + env(safe-area-inset-right, 0px)) 10px calc(12px + env(safe-area-inset-left, 0px))',
                minHeight: 56,
                background: posGradient(theme.palette.mode),
                color: '#fff',
                zIndex: 5
            })}
        >
            <BarButton label={backLabel} onClick={onBack}>
                <BackIcon size={20} />
            </BarButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box component="h1" sx={{ m: 0, fontSize: 17, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {title}
                </Box>
                {sub && <Box sx={{ fontSize: 11.5, color: 'text.disabled', mt: '1px', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</Box>}
            </Box>
            {actions}
        </Box>
    );
}

// `.content` do protótipo: rolagem vertical com 16px 16px 28px.
const ScrollBody = ({ children, scrollRef, sx }) => (
    <Box
        ref={scrollRef}
        sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            p: '16px 16px 28px',
            pl: 'calc(16px + env(safe-area-inset-left, 0px))',
            pr: 'calc(16px + env(safe-area-inset-right, 0px))',
            ...sx
        }}
    >
        {children}
    </Box>
);

// `.formbar` (+ `.pos`): rodapé em fluxo, fora da área rolável.
function FormBar({ pos, children }) {
    return (
        <Box
            sx={(theme) => ({
                flexShrink: 0,
                display: 'flex',
                gap: '10px',
                boxSizing: 'border-box',
                p: '10px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
                pl: 'calc(16px + env(safe-area-inset-left, 0px))',
                pr: 'calc(16px + env(safe-area-inset-right, 0px))',
                boxShadow: '0 -8px 24px rgba(15,23,42,.05)',
                zIndex: 5,
                ...(pos
                    ? { background: posGradient(theme.palette.mode), color: '#fff', border: 0 }
                    : { backgroundColor: theme.palette.background.paper, borderTop: '1px solid', borderColor: theme.palette.divider })
            })}
        >
            {children}
        </Box>
    );
}

// `.dialog`: cartão centralizado com ícone, título, texto e botões.
function KitDialog({ open, onClose, title, text, actions }) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: (theme) => ({
                        m: '20px',
                        width: 'calc(100% - 40px)',
                        maxWidth: 'calc(100% - 40px)',
                        borderRadius: '22px',
                        p: '22px 20px 16px',
                        textAlign: 'center',
                        backgroundColor: theme.palette.background.paper,
                        backgroundImage: 'none',
                        boxShadow: theme.customShadows.lg
                    })
                }
            }}
        >
            <Box
                sx={{ width: 56, height: 56, borderRadius: '50%', m: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'error.bg', color: 'error.main' }}
            >
                <AlertIcon size={26} />
            </Box>
            <Box component="h3" sx={{ m: '0 0 6px', fontSize: 17, fontWeight: 800, lineHeight: 1.4 }}>{title}</Box>
            <Box component="p" sx={{ m: '0 0 16px', color: 'text.secondary', fontSize: 13.5, lineHeight: 1.4 }}>{text}</Box>
            <Box sx={{ display: 'flex', gap: '10px', '& > *': { flex: 1 } }}>{actions}</Box>
        </Dialog>
    );
}

// Modal de alerta "Atenção" (mensagem + OK) reutilizável por outras telas mobile
// (ex.: operar/faturar OS), no mesmo visual do modal de erro da venda.
export function MobileAlertDialog({ open, message, onClose }) {
    return (
        <KitDialog
            open={open}
            onClose={onClose}
            title="Atenção"
            text={message}
            actions={<KitButton variant="primary" onClick={onClose} aria-label="Fechar mensagem de erro">OK</KitButton>}
        />
    );
}

// Botão `.btn.solid-danger`
const solidDangerSx = { backgroundColor: 'error.main', color: '#fff', border: 0 };

/* ------------------------------------------------------------------ */
/* Campo de desconto (máscara de moeda + alternância R$ / %)           */
/* ------------------------------------------------------------------ */

// InputDiscount do app: máscara de moeda (somente dígitos) + alternância R$ / %.
function maskDiscount(raw, isPct) {
    const digits = String(raw).replace(/\D/g, '').slice(0, 12);
    if (!digits) return '0,00';
    const v = parseInt(digits, 10);
    if (v < 100) return `0,${String(v).padStart(2, '0')}`;
    let reais = String(Math.floor(v / 100));
    const cents = String(v % 100).padStart(2, '0');
    if (!isPct) reais = reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${reais},${cents}`;
}

// `.field` + `.input` (+ `.input-ico`) com o mesmo comportamento do `DiscountInput`.
function PosDiscountField({ label, onChange, isRequired, allowPercent = true, sx }) {
    const [text, setText] = useState('0,00');
    const [pct, setPct] = useState(false);
    const [touched, setTouched] = useState(false);
    const error = isRequired && touched && text === '0,00';

    const emit = (value, isPct) => onChange(isPct ? `${value}%` : value, isPct);

    return (
        <KitField label={label} help={error ? 'Este campo é obrigatório' : undefined} error={error} sx={sx}>
            <Box sx={{ position: 'relative' }}>
                <KitInput
                    value={text}
                    error={error}
                    inputMode="numeric"
                    enterKeyHint="done"
                    aria-label={label}
                    onChange={(e) => {
                        const next = maskDiscount(e.target.value, pct);
                        setText(next);
                        setTouched(true);
                        emit(next, pct);
                    }}
                    sx={{ pl: pct ? '14px' : '44px', pr: allowPercent ? '52px' : '14px' }}
                />
                {!pct && (
                    <Box component="span" sx={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'text.disabled', fontWeight: 600, pointerEvents: 'none' }}>R$</Box>
                )}
                {allowPercent && (
                    <Box
                        component="button"
                        type="button"
                        aria-label={pct ? 'Mudar para valor' : 'Mudar para porcentagem'}
                        title={pct ? 'Mudar para valor' : 'Mudar para porcentagem'}
                        onClick={() => {
                            const nextPct = !pct;
                            setPct(nextPct);
                            if (text !== '0,00') emit(text, nextPct);
                        }}
                        sx={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 38, height: 38, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', border: 0, background: 'none', p: 0, cursor: 'pointer' }}
                    >
                        {pct ? <DollarIcon size={20} /> : <PercentIcon size={20} />}
                    </Box>
                )}
            </Box>
        </KitField>
    );
}

/* ------------------------------------------------------------------ */
/* DiscountInput (usado também pelo faturamento da OS)                 */
/* ------------------------------------------------------------------ */

export function DiscountInput({ label, onChange, isRequired }) {
    const [text, setText] = useState('0,00');
    const [pct, setPct] = useState(false);
    const [touched, setTouched] = useState(false);
    const error = isRequired && touched && text === '0,00';

    const emit = (value, isPct) => onChange(isPct ? `${value}%` : value, isPct);

    return (
        <TextField
            variant="standard"
            fullWidth
            label={label}
            value={text}
            error={error}
            helperText={error ? 'Este campo é obrigatório' : undefined}
            onChange={(e) => {
                const next = maskDiscount(e.target.value, pct);
                setText(next);
                setTouched(true);
                emit(next, pct);
            }}
            slotProps={{
                htmlInput: { inputMode: 'numeric', enterKeyHint: 'done' },
                input: {
                    startAdornment: pct ? undefined : <InputAdornment position="start">R$</InputAdornment>,
                    endAdornment: (
                        <InputAdornment position="end">
                            {pct && <Box component="span" sx={{ mr: 0.5 }}>%</Box>}
                            <Tooltip title={pct ? 'Mudar para valor' : 'Mudar para porcentagem'}>
                                <IconButton
                                    aria-label={pct ? 'Mudar para valor' : 'Mudar para porcentagem'}
                                    onClick={() => {
                                        const nextPct = !pct;
                                        setPct(nextPct);
                                        if (text !== '0,00') emit(text, nextPct);
                                    }}
                                    sx={{ color: '#2196F3' }}
                                >
                                    {pct ? <AttachMoney /> : <Percent />}
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    )
                }
            }}
        />
    );
}


// Mensagens amigáveis para falhas ao abrir a câmera (iOS/Android).
function cameraErrorMessage(e) {
    const name = e && e.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') {
        return 'Permissão da câmera negada. Libere o acesso à câmera nos Ajustes do aparelho/navegador e tente novamente.';
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        return 'Nenhuma câmera encontrada neste aparelho.';
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
        return 'A câmera está em uso por outro aplicativo. Feche-o e tente novamente.';
    }
    if (name === 'OverconstrainedError') {
        return 'Não foi possível usar a câmera traseira deste aparelho.';
    }
    return 'Erro ao escanear código de barras: não foi possível acessar a câmera';
}

// Tela de leitura de código de barras (BarcodeScannerScreen do app). Usa a API
// nativa BarcodeDetector quando existe (Android/Chrome); senão, cai para o
// @zxing/browser (iOS Safari/PWA), carregado sob demanda.
function BarcodeScanner({ onClose, onDetected, onError }) {
    const videoRef = useRef(null);

    useEffect(() => {
        let stream;
        let timer;
        let controls;
        let stopped = false;
        let done = false;

        const cleanup = () => {
            stopped = true;
            clearInterval(timer);
            if (controls) {
                try { controls.stop(); } catch (_) { /* já parado */ }
                controls = null;
            }
            if (stream) {
                stream.getTracks().forEach((t) => t.stop());
                stream = null;
            }
            const video = videoRef.current;
            if (video && video.srcObject) {
                try { video.srcObject.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
                video.srcObject = null;
            }
        };

        const handleCode = (value) => {
            if (done || stopped || !value) return;
            done = true;
            cleanup();
            try { if (navigator.vibrate) navigator.vibrate(80); } catch (_) { /* ignore */ }
            onDetected(value);
        };

        const openCamera = async () => {
            try {
                return await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
            } catch (e) {
                if (e && e.name === 'OverconstrainedError') {
                    return navigator.mediaDevices.getUserMedia({ video: true, audio: false });
                }
                throw e;
            }
        };

        const startNative = async () => {
            stream = await openCamera();
            if (stopped) {
                cleanup();
                return;
            }
            const video = videoRef.current;
            video.setAttribute('playsinline', '');
            video.muted = true;
            video.srcObject = stream;
            await video.play();
            const detector = new window.BarcodeDetector();
            timer = setInterval(async () => {
                try {
                    const codes = await detector.detect(video);
                    if (codes.length && codes[0].rawValue) handleCode(codes[0].rawValue);
                } catch (_) {
                    // frame sem leitura: tenta de novo no próximo ciclo
                }
            }, 300);
        };

        const startZxing = async () => {
            const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
                import('@zxing/browser'),
                import('@zxing/library')
            ]);
            if (stopped) return;
            const hints = new Map();
            hints.set(DecodeHintType.POSSIBLE_FORMATS, [
                BarcodeFormat.EAN_13,
                BarcodeFormat.EAN_8,
                BarcodeFormat.UPC_A,
                BarcodeFormat.UPC_E,
                BarcodeFormat.CODE_128,
                BarcodeFormat.CODE_39,
                BarcodeFormat.ITF,
                BarcodeFormat.QR_CODE
            ]);
            hints.set(DecodeHintType.TRY_HARDER, true);
            const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 150 });
            const video = videoRef.current;
            video.setAttribute('playsinline', '');
            video.muted = true;
            const onResult = (result) => {
                if (result) handleCode(result.getText());
            };
            let c;
            try {
                c = await reader.decodeFromConstraints({ video: { facingMode: { ideal: 'environment' } }, audio: false }, video, onResult);
            } catch (e) {
                if (e && e.name === 'OverconstrainedError' && !stopped) {
                    c = await reader.decodeFromConstraints({ video: true, audio: false }, video, onResult);
                } else {
                    throw e;
                }
            }
            if (stopped) {
                c.stop();
                return;
            }
            controls = c;
        };

        const start = async () => {
            try {
                if (typeof window.BarcodeDetector !== 'undefined') await startNative();
                else await startZxing();
            } catch (e) {
                if (stopped) return;
                cleanup();
                onError(cameraErrorMessage(e));
                onClose();
            }
        };
        start();
        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, backgroundColor: '#000', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ height: `calc(${APP_BAR_HEIGHT}px + env(safe-area-inset-top, 0px))`, pt: 'env(safe-area-inset-top, 0px)', boxSizing: 'border-box', flexShrink: 0, display: 'flex', alignItems: 'center', pl: 'calc(4px + env(safe-area-inset-left, 0px))', pr: 'calc(4px + env(safe-area-inset-right, 0px))', backgroundColor: '#000', color: '#fff' }}>
                <IconButton aria-label="Voltar" onClick={onClose} sx={appBarButtonSx}>
                    <ArrowBack />
                </IconButton>
                <Typography noWrap sx={{ flex: 1, ml: 1, fontSize: 20, fontWeight: 500, color: '#fff' }}>
                    Escanear Código de Barras
                </Typography>
            </Box>
            <Box component="video" ref={videoRef} playsInline muted autoPlay sx={{ flex: 1, minHeight: 0, width: '100%', objectFit: 'cover' }} />
        </Box>
    );
}



// `.item` com `.avatar` (36px) no lugar do ícone — lista de clientes.
function AvatarItem({ text, tone, title, sub, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                p: '13px 14px',
                minHeight: 60,
                width: '100%',
                boxSizing: 'border-box',
                border: 0,
                borderBottom: '1px solid',
                borderColor: 'divider',
                background: 'none',
                textAlign: 'left',
                fontFamily: 'inherit',
                color: 'text.primary',
                cursor: 'pointer',
                '&:last-child': { borderBottom: 0 },
                '&:active': { backgroundColor: 'background.subtle' }
            }}
        >
            <Box
                component="span"
                sx={(theme) => ({
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 13,
                    ...(tone === 'soft'
                        ? { backgroundColor: theme.palette.info.bg, color: theme.palette.primary.main }
                        : tone === 'purple'
                            ? { backgroundColor: theme.palette.secondary.bg, color: theme.palette.secondary.main }
                            : { backgroundImage: theme.palette.gradient.brand, color: '#fff' })
                })}
            >
                {text}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</Box>
                {sub && <Box sx={{ fontSize: 12, color: 'text.disabled', mt: '1px', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</Box>}
            </Box>
            <Box component="span" sx={{ display: 'inline-flex', color: 'text.disabled', flexShrink: 0 }}><RightIcon size={18} /></Box>
        </Box>
    );
}

/* ------------------------------------------------------------------ */
/* Tela "Pagamento" (vendas/pdv-pagamento.html)                        */
/* ------------------------------------------------------------------ */

const PAY_METHODS = [
    { type: 'pix_key', label: 'Pix (chave)', Icon: ZapIcon, apiType: 'PIXKEY' },
    { type: 'pix_qr', label: 'Pix QR Code', Icon: ZapIcon, apiType: 'PIXQRCODE' },
    { type: 'cash', label: 'Dinheiro', Icon: DollarIcon, apiType: 'DINHEIRO' },
    { type: 'debit', label: 'Débito', Icon: CardIcon, apiType: 'DÉBITO' },
    { type: 'credit', label: 'Crédito', Icon: CardIcon, apiType: 'CRÉDITO' }
];

// Linha de "Pagamentos adicionados" (`.item` com `.ico`, `.t`, `.s`, `.end .v`).
function paymentCardInfo(payment) {
    switch (payment.type) {
        case 'credit':
            return { name: 'Crédito', details: String(payment.installment ?? ''), Icon: CardIcon, tone: 'purple' };
        case 'debit':
            return { name: 'Débito', details: 'À vista', Icon: CardIcon };
        case 'pix_key':
            return { name: 'Pix (chave)', details: 'Pagamento via chave PIX', Icon: ZapIcon };
        case 'pix_qrcode':
            return { name: 'Pix QR Code', details: 'Pagamento via QR Code', Icon: ZapIcon };
        case 'cash':
            return { name: 'Dinheiro', details: payment.change ? `Troco: R$ ${payment.change}` : 'Recebido', Icon: DollarIcon, tone: 'green' };
        default:
            return { name: 'Pagamento', details: '', Icon: CardIcon };
    }
}

// `.opt` (+ `.active`, `small`)
function PayOption({ active, icon: Icon, label, hint, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={active}
            sx={(theme) => ({
                border: '1.5px solid',
                borderColor: active ? theme.palette.primary.main : theme.palette.divider,
                borderRadius: '14px',
                p: '12px',
                backgroundColor: active ? theme.palette.info.bg : theme.palette.background.paper,
                color: active ? theme.palette.primary.main : theme.palette.text.primary,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '6px',
                fontWeight: 700,
                fontSize: 13,
                lineHeight: 'normal',
                textAlign: 'left',
                position: 'relative',
                fontFamily: 'inherit',
                cursor: 'pointer',
                boxSizing: 'border-box'
            })}
        >
            <Box component="span" sx={{ display: 'inline-flex', color: active ? 'primary.main' : 'text.disabled' }}><Icon size={20} /></Box>
            {label}
            {hint && <Box component="small" sx={{ fontWeight: 500, color: 'text.disabled', fontSize: 11.5 }}>{hint}</Box>}
        </Box>
    );
}

// Reutilizada também pelo faturamento da OS no mobile (`PaymentModal`), que passa
// `afterSummary` (desconto / tipo de pagamento), `canFinishOverride` e `finishLabel` — todos
// opcionais; sem eles o comportamento é o da venda.
export function PaymentScreen(props) {
    const {
        saleState, payments, machineCards, installments, selectedMachine, setSelectedMachine, uiState, setUiState,
        creditValue, setCreditValue, debitValue, setDebitValue, pixValue, setPixValue, cashValue, setCashValue,
        loading, getPaymentBaseTotal, onBack, onAddPayment, onRemovePayment, onComplete, onGetInstallments, toast,
        title = 'Pagamento', afterSummary, canFinishOverride, finishLabel = 'Finalizar venda', itemsCount, customerName
    } = props;
    const theme = useTheme();
    const [payType, setPayType] = useState('');
    const detailsRef = useRef(null);

    const remaining = saleState.remaining_value;
    const baseTotal = getPaymentBaseTotal();
    const canFinish = canFinishOverride !== undefined ? canFinishOverride : remaining === '0,00' && payments.length > 0;
    const paid = payments.length > 0 ? Math.max(0, parseMoney(baseTotal) - parseMoney(remaining)) : 0;

    const valueByType = { credit: creditValue, debit: debitValue, pix_key: pixValue, pix_qr: pixValue, cash: cashValue };
    const setValueByType = { credit: setCreditValue, debit: setDebitValue, pix_key: setPixValue, pix_qr: setPixValue, cash: setCashValue };
    const currentValue = valueByType[payType] ?? '';
    const machineValue = machineCards.some((m) => m.ID === selectedMachine) ? selectedMachine : '';
    const selectedCard = machineCards.find((m) => m.ID === selectedMachine);
    const maxInstallments = machineCards.reduce((max, m) => Math.max(max, Number(m.MaxAmountInstallment) || 0), 0);

    // Mantém a parcela escolhida válida quando a lista de parcelas muda (o app seleciona a primeira).
    useEffect(() => {
        if (installments.length > 0 && !installments.includes(uiState.installments)) {
            setUiState((prev) => ({ ...prev, installments: installments[0] }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installments]);

    const selectMethod = (type) => {
        if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
        setPayType(type);
        // preenche o valor com o restante da venda (como o app)
        setValueByType[type](remaining);
        setTimeout(() => {
            if (detailsRef.current) detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    const handleValueChange = (type, value) => {
        setValueByType[type](value);
        // recalcula as parcelas se a maquininha já estiver escolhida (como o app)
        if (type === 'credit' && selectedCard && value && value !== '0,00') {
            onGetInstallments(saleState.id, value, selectedCard.MaxAmountInstallment).catch(() => {});
        }
    };

    const handleAdd = async () => {
        if (!payType) {
            toast('Por favor, selecione uma forma de pagamento', 'error');
            return;
        }
        if (['credit', 'debit', 'pix_qr'].includes(payType) && !machineValue) {
            toast('Por favor, selecione uma maquininha', 'error');
            return;
        }
        if (!currentValue || currentValue === '0,00') {
            toast('Por favor, informe o valor do pagamento', 'error');
            return;
        }
        if (payType === 'credit' && !installments.includes(uiState.installments)) {
            toast('Por favor, selecione o número de parcelas', 'error');
            return;
        }
        const method = PAY_METHODS.find((m) => m.type === payType);
        try {
            await onAddPayment(method.apiType, currentValue, payType === 'credit' ? uiState.installments : '1');
        } catch (error) {
            toast(error?.response?.data?.error || error?.message || 'Erro ao adicionar pagamento', 'error');
            return;
        }
        toast('Pagamento adicionado', 'success');
        setPayType('');
        setSelectedMachine('');
        if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    };

    const needsMachine = ['credit', 'debit', 'pix_qr'].includes(payType);
    const machineIndex = machineCards.findIndex((m) => m.ID === machineValue);
    const installmentIndex = installments.indexOf(uiState.installments);
    const subLine = [
        itemsCount !== undefined && itemsCount !== null ? `${itemsCount} ${itemsCount === 1 ? 'item' : 'itens'}` : null,
        customerName
    ].filter(Boolean).join(' · ');

    return (
        <ScreenShell>
            <PosAppBar title={title} onBack={onBack} />
            <ScrollBody>
                {/* Total a pagar (`.card.pos`) */}
                <KitCard sx={{ background: posGradient(theme.palette.mode), color: '#fff', border: 0, textAlign: 'center' }}>
                    <Box sx={{ fontSize: 12.5, opacity: 0.8, lineHeight: 1.4 }}>Total a pagar</Box>
                    <Box sx={{ fontSize: 38, fontWeight: 800, letterSpacing: '-.03em', color: POS_GRADIENT.posTotal, lineHeight: 1.4 }}>R$ {baseTotal}</Box>
                    {subLine && <Box sx={{ fontSize: 12.5, opacity: 0.8, lineHeight: 1.4 }}>{subLine}</Box>}
                </KitCard>

                {afterSummary && <Box sx={{ mt: '12px' }}>{afterSummary}</Box>}

                <SecH title="Forma de pagamento" />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {PAY_METHODS.map(({ type, label, Icon }) => (
                        <PayOption
                            key={type}
                            active={payType === type}
                            icon={Icon}
                            label={label}
                            hint={type === 'credit' && maxInstallments > 1 ? `até ${maxInstallments}x` : undefined}
                            onClick={() => selectMethod(type)}
                        />
                    ))}
                </Box>

                <SecH title="Pagamentos adicionados" />
                <KitList>
                    {payments.length === 0 && <KitItem icon={CardIcon} tone="gray" title="Nenhum pagamento adicionado" />}
                    {payments.map((payment, index) => {
                        const info = paymentCardInfo(payment);
                        return (
                            <KitItem
                                key={`${payment.type}-${index}`}
                                icon={info.Icon}
                                tone={info.tone}
                                title={info.name}
                                sub={info.details}
                                end={(
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>R$ {payment.value}</Box>
                                        <Box
                                            component="button"
                                            type="button"
                                            aria-label={`Remover pagamento ${info.name}`}
                                            onClick={() => onRemovePayment(index)}
                                            sx={{ width: 32, height: 32, borderRadius: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'error.main', border: 0, background: 'none', p: 0, cursor: 'pointer' }}
                                        >
                                            <TrashIcon size={18} />
                                        </Box>
                                    </Box>
                                )}
                            />
                        );
                    })}
                </KitList>

                <KitCard sx={{ mt: '12px' }}>
                    <KitKV totals label="Pago" value={`R$ ${formatMoney(paid)}`} />
                    <KitKV totals label="Restante" value={<Box component="b" sx={{ color: WARN_LIGHT }}>R$ {remaining}</Box>} />
                </KitCard>

                <Box ref={detailsRef}>
                    <KitField label="Valor a receber" help="Troco será calculado automaticamente para dinheiro">
                        <KitInput
                            value={currentValue}
                            disabled={!payType}
                            inputMode="numeric"
                            enterKeyHint="done"
                            aria-label="Valor a receber"
                            onChange={(e) => handleValueChange(payType, formatCurrency(e.target.value))}
                        />
                    </KitField>
                    {payType === 'pix_key' && (
                        <Box sx={{ mb: '14px' }}>
                            <KitBanner tone="info" title="Pagamento via chave PIX">
                                Aguarde a confirmação do pagamento antes de finalizar.
                            </KitBanner>
                        </Box>
                    )}
                    {needsMachine && (
                        <KitField label="Maquininha" help={machineCards.length === 0 ? 'Nenhuma maquininha disponível' : undefined}>
                            <KitSelect
                                value={machineIndex >= 0 ? String(machineIndex) : ''}
                                disabled={machineCards.length === 0}
                                aria-label="Maquininha"
                                onChange={(e) => {
                                    const machine = machineCards[Number(e.target.value)];
                                    const id = machine ? machine.ID : '';
                                    if (payType === 'credit') props.onChangeMachine(id);
                                    else setSelectedMachine(id);
                                }}
                            >
                                <option value="">Selecione</option>
                                {machineCards.map((m, index) => (
                                    <option key={m.ID} value={index}>{m.Name}</option>
                                ))}
                            </KitSelect>
                        </KitField>
                    )}
                    {payType === 'credit' && machineValue && currentValue && currentValue !== '0,00' && (
                        <KitField label="Parcelas">
                            <KitSelect
                                value={installmentIndex >= 0 ? String(installmentIndex) : ''}
                                aria-label="Parcelas"
                                onChange={(e) => setUiState((prev) => ({ ...prev, installments: installments[Number(e.target.value)] }))}
                            >
                                {installments.map((n, index) => (
                                    <option key={`${n}-${index}`} value={index}>{`${n}`}</option>
                                ))}
                            </KitSelect>
                        </KitField>
                    )}
                </Box>
            </ScrollBody>

            <FormBar>
                <KitButton onClick={handleAdd} disabled={!payType} sx={{ flex: 1 }}>Adicionar pagamento</KitButton>
                <KitButton
                    onClick={onComplete}
                    disabled={!canFinish}
                    sx={{ flex: 1, backgroundColor: 'success.main', color: '#fff', border: 0 }}
                >
                    {finishLabel}
                </KitButton>
            </FormBar>

            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 30,
                        backgroundColor: 'rgba(10,14,26,.5)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2
                    }}
                >
                    <CircularProgress sx={{ color: '#fff' }} />
                    <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>Processando pagamento...</Typography>
                </Box>
            )}
        </ScreenShell>
    );
}

/* ------------------------------------------------------------------ */
/* Esqueleto de carregamento inicial                                   */
/* ------------------------------------------------------------------ */

export function MobileSaleSkeleton() {
    return (
        <ScreenShell>
            <PosAppBar title="Nova venda" onBack={() => window.history.back()} />
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        </ScreenShell>
    );
}

/* ------------------------------------------------------------------ */
/* Tela "Nova venda" (vendas/pdv.html)                                 */
/* ------------------------------------------------------------------ */

// `.pcard`: cartão de produto da grade (thumb 96px, nome, preço, estoque, selo de quantidade).
function ProductCard({ product, qtyInCart, onClick }) {
    const generic = Boolean(product.is_generic || product.isGeneric);
    const stockText = generic ? 'Valor livre' : 'Em estoque';
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                display: 'block',
                position: 'relative',
                width: '100%',
                p: 0,
                textAlign: 'left',
                fontFamily: 'inherit',
                cursor: 'pointer',
                backgroundColor: 'background.paper',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 1px 2px rgba(15,23,42,.06)',
                boxSizing: 'border-box'
            }}
        >
            {qtyInCart > 0 && (
                <Box
                    component="span"
                    sx={{ position: 'absolute', top: '8px', right: '8px', backgroundColor: 'primary.main', color: '#fff', borderRadius: '11px', minWidth: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 800, p: '0 6px', boxSizing: 'border-box', zIndex: 1 }}
                >
                    {qtyInCart}
                </Box>
            )}
            <Box sx={{ height: 96, backgroundColor: 'background.subtle', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', overflow: 'hidden' }}>
                {product.image ? (
                    <Box component="img" src={product.image} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <PackageIcon size={34} />
                )}
            </Box>
            <Box sx={{ p: '10px 12px 12px' }}>
                <Box
                    sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.25, height: 33, overflow: 'hidden', wordBreak: 'break-word' }}
                >
                    {product.name}
                </Box>
                <Box sx={{ fontWeight: 800, fontSize: 15, mt: '6px', letterSpacing: '-.02em', lineHeight: 1.4 }}>R$ {product.price}</Box>
                <Box sx={{ fontSize: 11.5, lineHeight: 1.4 }}>{stockText}</Box>
            </Box>
        </Box>
    );
}

// Linha de ação dentro de uma folha (`.action-list .item`).
function ActionRow({ icon, tone, title, sub, onClick }) {
    return <KitItem icon={icon} tone={tone} title={title} sub={sub} chevron onClick={onClick} sx={{ border: 0, borderBottom: 0, p: '12px 4px' }} />;
}

// `.cart-line`
function CartLine({ item, busy, canSell, onMinus, onPlus, onOptions }) {
    const name = item.product_name || item.name || '';
    const quantity = parseInt(item.quantity, 10) || 0;
    const discount = item.item_discount_type || '';
    return (
        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', p: '12px 0', borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
            <Box component="span" sx={{ width: 44, height: 44, borderRadius: '12px', backgroundColor: 'background.subtle', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', flexShrink: 0, overflow: 'hidden' }}>
                {item.image ? (
                    <Box component="img" src={item.image} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <PackageIcon size={20} />
                )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 700, lineHeight: 1.4, wordBreak: 'break-word' }}>{name}</Box>
                <Box sx={{ fontSize: 12.5, color: 'text.disabled', lineHeight: 1.4 }}>
                    R$ {item.price} un{discount ? ` · Desc: ${discount}` : ''}
                </Box>
            </Box>
            {canSell && (
                <Box
                    component="button"
                    type="button"
                    aria-label={`Opções de ${name}`}
                    onClick={onOptions}
                    sx={{ width: 32, height: 32, borderRadius: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', border: 0, background: 'none', p: 0, cursor: 'pointer', flexShrink: 0 }}
                >
                    <DotsIcon size={18} />
                </Box>
            )}
            {canSell ? (
                <KitStepper
                    small
                    value={quantity}
                    onMinus={onMinus}
                    onPlus={onPlus}
                    minusDisabled={busy}
                    plusDisabled={busy}
                    minusLabel={`Diminuir quantidade de ${name}`}
                    plusLabel={`Aumentar quantidade de ${name}`}
                />
            ) : (
                <Box component="b" sx={{ minWidth: 28, textAlign: 'center', fontWeight: 800 }}>{quantity}</Box>
            )}
        </Box>
    );
}

/* ------------------------------------------------------------------ */
/* Componente exportado                                                */
/* ------------------------------------------------------------------ */

export default function MobileSaleView(props) {
    const {
        saleState, setSaleState, uiState, setUiState, discountState, payments, loading, setLoading, loadingState,
        updatingItems, deletingItems, hasPermission, canApplyItemDiscount, canCreateCustomer, identifyCustomerEnabled,
        printReceiptEnabled, saleCustomer, customerOptions, customerToOption, colorMode, toggleColorMode, onExit,
        itemDiscountDialog, setItemDiscountDialog, openItemDiscountDialog, handleSubmitItemDiscount,
        openEditDialog, editingItem, editFormData, setEditFormData, handleEditItem, handleSaveEdit, handleCancelEdit,
        openDialogCancelSale, setOpenDialogCancelSale, handleCancelSale,
        openDialogGenericProduct, genericProductData, setGenericProductData, handleConfirmGenericProduct, handleCancelGenericProduct,
        handleIncrementItem, handleDecrementItem, handleDeleteItem, handleSelectProduct, handleSearchProduct,
        handleApplyDiscount, handleRemoveSaleDiscount, handlePrint, handleChangeSaleCustomer, setOpenDialogNewCustomer,
        handleSelectCashRegister, setSelectedMachine, selectedMachine, machineCards, installments,
        creditValue, setCreditValue, debitValue, setDebitValue, pixValue, setPixValue, cashValue, setCashValue,
        getPaymentBaseTotal, onOpenPayment, onClosePayment, handleAddPayment, handleRemovePayment, handleChangeMachine,
        handleGetInstallments, handleConfirmFinishSale, printSaleById, setSaleHasError
    } = props;

    useLockZoom();

    const [optionsOpen, setOptionsOpen] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    const [customerSheetOpen, setCustomerSheetOpen] = useState(false);
    const [customerQuery, setCustomerQuery] = useState('');
    const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
    const [discountValue, setDiscountValue] = useState('');
    const [cashDialogOpen, setCashDialogOpen] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [completedSale, setCompletedSale] = useState(null);
    const [cancelReasonError, setCancelReasonError] = useState(false);
    const [qtyText, setQtyText] = useState('');
    const [itemMenu, setItemMenu] = useState(null);
    const [category, setCategory] = useState('');
    const [visibleCount, setVisibleCount] = useState(PRODUCTS_PAGE);
    const [addProduct, setAddProduct] = useState(null);
    const [addQty, setAddQty] = useState(1);
    const [addDiscount, setAddDiscount] = useState('0,00');
    const [adding, setAdding] = useState(false);
    // Na venda não há toasts: só erros/avisos que impedem a ação, em modal de alerta.
    // Um único estado => erros em sequência substituem a mensagem (não empilham modais).
    const [alertState, setAlertState] = useState({ open: false, message: '' });

    const toast = useCallback((message, severity = 'info') => {
        if (severity !== 'error' && severity !== 'warning') return;
        setAlertState({ open: true, message: message || 'Ocorreu um erro. Tente novamente.' });
    }, []);
    const closeAlert = () => setAlertState((prev) => ({ ...prev, open: false }));

    const cart = saleState.cart;
    const canSell = hasPermission('sale.create');
    const canSaleDiscount = hasPermission('sale.discount.sale');
    const canCancel = hasPermission('sale.cancel');
    const discountApplied = discountState.applied;
    const search = uiState.searchProduct || '';

    // campo de quantidade do diálogo de edição (texto livre; valida ao salvar)
    useEffect(() => {
        if (openEditDialog) setQtyText(String(editFormData.quantity ?? ''));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openEditDialog]);

    const itemsCount = cart.reduce((acc, item) => acc + (parseInt(item.quantity, 10) || 0), 0);
    const itemDiscount = saleState.total_item_discount || '0,00';
    const saleDiscount = discountApplied ? saleState.discount_applied || '0,00' : '0,00';
    const totalDiscount = formatMoney(parseMoney(itemDiscount) + parseMoney(saleDiscount));
    const totalShown = discountApplied ? saleState.total_discount : saleState.total;
    const customerLabel = saleCustomer ? saleCustomer.name : 'Consumidor final';

    const closeAttention = () => setUiState((prev) => ({ ...prev, showAttention: false }));

    const handleSearchChange = (value) => {
        setUiState((prev) => ({ ...prev, searchProduct: value }));
        setVisibleCount(PRODUCTS_PAGE);
    };

    const handleSubmitSearch = (value) => {
        if (!value || !value.trim()) return;
        handleSearchProduct(value.trim());
        if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    };

    const handleScan = () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            toast('Câmera indisponível neste navegador. A leitura exige HTTPS e permissão de câmera. Digite o código do produto.', 'warning');
            return;
        }
        setScannerOpen(true);
    };

    /* ----- catálogo (grade de produtos) ----- */
    const productList = useMemo(() => saleState.productList || [], [saleState.productList]);

    const categories = useMemo(() => {
        const names = new Set();
        productList.forEach((p) => {
            if (p.category_name) names.add(p.category_name);
        });
        return Array.from(names).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [productList]);

    const filteredProducts = useMemo(() => {
        const q = search.trim().toLowerCase();
        return productList.filter((p) => {
            if (category && p.category_name !== category) return false;
            if (!q) return true;
            return (p.name || '').toLowerCase().includes(q) || String(p.code || '').toLowerCase().includes(q);
        });
    }, [productList, category, search]);

    const qtyByProduct = useMemo(() => {
        const map = {};
        cart.forEach((item) => {
            map[item.product_id] = (map[item.product_id] || 0) + (parseInt(item.quantity, 10) || 0);
        });
        return map;
    }, [cart]);

    /* ----- folha "Adicionar à venda" ----- */
    const openAdd = (product) => {
        if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
        if (product.is_generic || product.isGeneric) {
            // produto genérico: abre o formulário de produto avulso (mesmo fluxo de antes)
            handleSelectProduct({ id: product.id });
            return;
        }
        setAddQty(1);
        setAddDiscount('0,00');
        setAddProduct(product);
    };

    const stockAvailable = addProduct && Number.isFinite(Number(addProduct.quantity)) && addProduct.quantity !== null && addProduct.quantity !== undefined
        ? Number(addProduct.quantity) - (qtyByProduct[addProduct.id] || 0)
        : Infinity;

    const confirmAdd = async () => {
        if (!addProduct || adding) return;
        const product = addProduct;
        const discount = canApplyItemDiscount && hasMoney(addDiscount) ? addDiscount : '';
        setAdding(true);
        try {
            await handleSelectProduct({ id: product.id }, { quantity: addQty, discount });
            setAddProduct(null);
            // sucesso sem toast: o carrinho já reflete o item adicionado
        } finally {
            setAdding(false);
        }
    };

    /* ----- cliente ----- */
    const customerRows = useMemo(() => {
        const q = customerQuery.trim().toLowerCase();
        return (customerOptions || [])
            .map(customerToOption)
            .filter((c) => !q || (c.name || '').toLowerCase().includes(q) || String(c.sub || '').toLowerCase().includes(q))
            .slice(0, CUSTOMERS_LIMIT);
    }, [customerOptions, customerQuery, customerToOption]);

    const chooseCustomer = (option) => {
        setCustomerSheetOpen(false);
        handleChangeSaleCustomer(option);
    };

    /* ----- cancelar / editar / pagamento ----- */
    const handleCancelConfirm = async () => {
        const reason = String(saleState.reason || '').trim();
        if (!reason) {
            setCancelReasonError(true);
            return;
        }
        setCancelReasonError(false);
        try {
            await handleCancelSale();
        } catch (error) {
            setLoading(false);
            toast(error?.response?.data?.error || 'Erro ao cancelar a venda', 'error');
        }
    };

    const handleCompletePayment = async () => {
        const finishedSale = { id: saleState.id, number: saleState.number, total: totalShown };
        try {
            await handleConfirmFinishSale(false);
        } catch (error) {
            setLoading(false);
            toast(error?.response?.data?.error || error?.message || 'Erro ao completar pagamento', 'error');
            return;
        }
        setCompletedSale(finishedSale);
    };

    const handlePrintAnswer = async (shouldPrint) => {
        const saleIdToPrint = completedSale && completedSale.id;
        setCompletedSale(null);
        if (!shouldPrint) return;
        try {
            await printSaleById(saleIdToPrint);
        } catch (error) {
            toast('Erro ao imprimir venda', 'error');
        }
    };

    const handleSaveEditMobile = async () => {
        const n = parseInt(qtyText, 10);
        if (!qtyText.trim()) {
            toast('Quantidade é obrigatória', 'error');
            return;
        }
        if (!Number.isFinite(n) || n <= 0) {
            toast('Quantidade deve ser um número maior que zero', 'error');
            return;
        }
        if (editingItem?.is_generic && !String(editFormData.name || '').trim()) {
            setUiState((prev) => ({ ...prev, showAttention: true, attentionMessage: 'O nome do produto é obrigatório' }));
            return;
        }
        await handleSaveEdit();
    };

    const itemDiscountValid = Boolean(itemDiscountDialog.value) && itemDiscountDialog.value !== '0,00' && itemDiscountDialog.value !== '0,00%';
    const itemDiscountCurrent = itemDiscountDialog.item?.item_discount_type || '';
    const discountValid = Boolean(discountValue) && discountValue !== '0,00' && discountValue !== '0,00%';

    const errorDialog = (open, message, onClose) => (
        <KitDialog
            open={open}
            onClose={onClose}
            title="Atenção"
            text={message}
            actions={<KitButton variant="primary" onClick={onClose} aria-label="Fechar mensagem de erro">OK</KitButton>}
        />
    );

    // Modal dos erros locais da venda/pagamento (antes eram toasts)
    const alertNode = errorDialog(alertState.open, alertState.message, closeAlert);

    // Folha "Venda concluída!" (`#ok` do protótipo de pagamento)
    const completedSheet = (
        <MobileSheet
            open={Boolean(completedSale)}
            onClose={() => handlePrintAnswer(false)}
            title="Venda concluída!"
            footer={(
                <>
                    {printReceiptEnabled && (
                        <KitButton sx={{ flex: 1 }} onClick={() => handlePrintAnswer(true)}>
                            <PrintIcon size={18} /> Imprimir
                        </KitButton>
                    )}
                    <KitButton variant="primary" sx={{ flex: 1 }} onClick={() => handlePrintAnswer(false)}>Nova venda</KitButton>
                </>
            )}
        >
            <Box sx={{ textAlign: 'center', p: '10px 0' }}>
                <Box
                    component="span"
                    sx={{ width: 76, height: 76, borderRadius: '50%', backgroundColor: 'success.bg', color: 'success.main', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', m: '0 auto 10px' }}
                >
                    <CheckIcon size={36} />
                </Box>
                <Box sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.4 }}>
                    Venda{completedSale && completedSale.number ? ` #${completedSale.number}` : ''} · R$ {completedSale ? completedSale.total : '0,00'}
                </Box>
                <Box sx={{ fontSize: 12.5, color: 'text.disabled', lineHeight: 1.4 }}>Estoque atualizado</Box>
            </Box>
        </MobileSheet>
    );

    /* ----- tela de pagamento ----- */
    if (uiState.openPayment) {
        return (
            <>
                <PaymentScreen
                    saleState={saleState}
                    payments={payments}
                    machineCards={machineCards}
                    installments={installments}
                    selectedMachine={selectedMachine}
                    setSelectedMachine={setSelectedMachine}
                    uiState={uiState}
                    setUiState={setUiState}
                    discountState={discountState}
                    creditValue={creditValue}
                    setCreditValue={setCreditValue}
                    debitValue={debitValue}
                    setDebitValue={setDebitValue}
                    pixValue={pixValue}
                    setPixValue={setPixValue}
                    cashValue={cashValue}
                    setCashValue={setCashValue}
                    loading={loading}
                    getPaymentBaseTotal={getPaymentBaseTotal}
                    onBack={onClosePayment}
                    onAddPayment={handleAddPayment}
                    onRemovePayment={handleRemovePayment}
                    onComplete={handleCompletePayment}
                    onGetInstallments={handleGetInstallments}
                    onChangeMachine={handleChangeMachine}
                    toast={toast}
                    itemsCount={itemsCount}
                    customerName={customerLabel}
                />
                {completedSheet}
                {errorDialog(uiState.showAttention, uiState.attentionMessage, closeAttention)}
                {errorDialog(Boolean(saleState.hasError), saleState.errorMessage, () => setSaleHasError(false))}
                {alertNode}
            </>
        );
    }

    /* ----- tela "Nova venda" ----- */
    return (
        <ScreenShell>
            <PosAppBar
                title="Nova venda"
                sub={`Cliente: ${customerLabel}`}
                backLabel="Voltar para o início"
                onBack={onExit}
                actions={(
                    <>
                        {identifyCustomerEnabled && (
                            <BarButton label="Cliente" onClick={() => { setCustomerQuery(''); setCustomerSheetOpen(true); }}>
                                <UsersIcon size={20} />
                            </BarButton>
                        )}
                        <BarButton label="Opções da venda" onClick={() => setOptionsOpen(true)}>
                            <DotsIcon size={20} />
                        </BarButton>
                    </>
                )}
            />

            <ScrollBody>
                <KitStickyToolbar top="-16px">
                    <KitSearch
                        value={search}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSubmitSearch(e.target.value);
                        }}
                        placeholder="Buscar produto ou ler código…"
                        inputProps={{ 'aria-label': 'Buscar produto por nome ou código', enterKeyHint: 'search', autoComplete: 'off' }}
                    />
                    <KitToolbarButton aria-label="Escanear código de barras" onClick={handleScan}>
                        <ScanIcon size={20} />
                    </KitToolbarButton>
                </KitStickyToolbar>

                {/* Produto genérico: formulário avulso logo abaixo da busca */}
                {openDialogGenericProduct && (
                    <KitCard sx={{ mb: '12px' }}>
                        <KitField label="Nome do produto">
                            <KitInput
                                value={genericProductData.product_name}
                                disabled={!hasPermission('sale.generic.rename') || loading}
                                aria-label="Nome do produto"
                                onChange={(e) => setGenericProductData((prev) => ({ ...prev, product_name: e.target.value }))}
                            />
                        </KitField>
                        <KitField label="Valor">
                            <KitInput
                                value={genericProductData.price}
                                disabled={loading}
                                inputMode="numeric"
                                aria-label="Valor"
                                onChange={(e) => setGenericProductData((prev) => ({ ...prev, price: formatCurrency(String(e.target.value)) }))}
                            />
                        </KitField>
                        <Box sx={{ display: 'flex', gap: '10px' }}>
                            <KitButton sx={{ flex: 1 }} disabled={loading} onClick={handleCancelGenericProduct}>Cancelar</KitButton>
                            <KitButton variant="primary" sx={{ flex: 1 }} disabled={loading} onClick={handleConfirmGenericProduct}>Adicionar à venda</KitButton>
                        </Box>
                    </KitCard>
                )}

                <KitChips>
                    <KitChip active={!category} onClick={() => { setCategory(''); setVisibleCount(PRODUCTS_PAGE); }}>Todos</KitChip>
                    {categories.map((name) => (
                        <KitChip key={name} active={category === name} onClick={() => { setCategory(name); setVisibleCount(PRODUCTS_PAGE); }}>{name}</KitChip>
                    ))}
                </KitChips>

                {filteredProducts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: '36px 20px', color: 'text.secondary', mt: '12px' }}>
                        <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', m: '0 auto 14px' }}>
                            <SearchIcon size={28} />
                        </Box>
                        <Box component="h3" sx={{ m: '0 0 4px', fontSize: 16, fontWeight: 800, color: 'text.primary', lineHeight: 1.4 }}>Nenhum produto encontrado</Box>
                        <Box sx={{ fontSize: 13, lineHeight: 1.4 }}>Busque pelo nome, digite o código e pressione Enter ou escaneie o código de barras.</Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', mt: '12px' }}>
                        {filteredProducts.slice(0, visibleCount).map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                qtyInCart={qtyByProduct[product.id] || 0}
                                onClick={() => openAdd(product)}
                            />
                        ))}
                    </Box>
                )}

                {filteredProducts.length > visibleCount && (
                    <KitButton block sx={{ mt: '14px' }} onClick={() => setVisibleCount((n) => n + PRODUCTS_PAGE)}>
                        Mostrar mais produtos
                    </KitButton>
                )}
            </ScrollBody>

            {/* Rodapé do PDV (`.formbar.pos`): itens, total e carrinho */}
            <FormBar pos>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box component="small" sx={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,.75)', fontWeight: 600, lineHeight: 1.4 }}>
                        {itemsCount} {itemsCount === 1 ? 'item' : 'itens'}
                    </Box>
                    <Box component="b" sx={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1.4 }} aria-label={`Total da venda: R$ ${totalShown}`}>
                        R$ {totalShown}
                    </Box>
                </Box>
                <KitButton variant="white" sx={{ flex: 1 }} onClick={() => setCartOpen(true)}>
                    <CartIcon size={18} /> Ver carrinho
                </KitButton>
            </FormBar>

            {loadingState.isAddingItem && (
                <Box
                    role="status"
                    aria-label="Adicionando produto, aguarde"
                    sx={{ position: 'absolute', inset: 0, zIndex: 30, backgroundColor: 'rgba(10,14,26,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <Box sx={{ px: 4, py: 3, borderRadius: '22px', backgroundColor: 'background.paper', boxShadow: (theme) => theme.customShadows.lg, textAlign: 'center' }}>
                        <CircularProgress size={36} thickness={4} />
                        <Typography sx={{ mt: 2, fontSize: 14, fontWeight: 700 }}>Adicionando produto...</Typography>
                    </Box>
                </Box>
            )}

            {/* Folha "Adicionar à venda" (#add) */}
            <MobileSheet
                open={Boolean(addProduct)}
                onClose={() => setAddProduct(null)}
                title={addProduct ? addProduct.name : ''}
                footer={(
                    <KitButton variant="primary" sx={{ flex: 1 }} disabled={adding || stockAvailable < 1} onClick={confirmAdd}>
                        Adicionar à venda
                    </KitButton>
                )}
            >
                {addProduct && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                            <Box>
                                <Box sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.4 }}>R$ {addProduct.price}</Box>
                                {addProduct.quantity !== undefined && addProduct.quantity !== null && (
                                    <Box sx={{ fontSize: 12.5, lineHeight: 1.4, color: Number(addProduct.quantity) <= 0 ? WARN_LIGHT : 'text.disabled', fontWeight: Number(addProduct.quantity) <= 0 ? 700 : undefined }}>
                                        {addProduct.quantity} em estoque
                                    </Box>
                                )}
                            </Box>
                            <KitStepper
                                value={addQty}
                                onMinus={() => setAddQty((q) => Math.max(1, q - 1))}
                                onPlus={() => setAddQty((q) => Math.min(q + 1, Math.max(1, stockAvailable)))}
                                minusDisabled={addQty <= 1}
                                plusDisabled={addQty >= stockAvailable}
                                minusLabel="Diminuir quantidade"
                                plusLabel="Aumentar quantidade"
                            />
                        </Box>
                        {canApplyItemDiscount && (
                            <Box sx={{ mt: '14px' }}>
                                <PosDiscountField label="Desconto (R$)" allowPercent={false} onChange={(value) => setAddDiscount(value)} />
                            </Box>
                        )}
                    </>
                )}
            </MobileSheet>

            {/* Folha "Carrinho" (#carrinho) */}
            <MobileSheet
                open={cartOpen}
                onClose={() => setCartOpen(false)}
                title="Carrinho"
                footer={(
                    <KitButton
                        variant="primary"
                        sx={{ flex: 1 }}
                        disabled={cart.length === 0}
                        onClick={() => {
                            setCartOpen(false);
                            onOpenPayment();
                        }}
                    >
                        Continuar para pagamento
                    </KitButton>
                )}
            >
                {cart.length === 0 && (
                    <Box sx={{ textAlign: 'center', p: '24px 0', color: 'text.disabled', fontSize: 13, lineHeight: 1.4 }}>Nenhum produto adicionado</Box>
                )}
                {cart.map((item) => {
                    const busyKey = `${item.product_id}-${item.name}`;
                    return (
                        <CartLine
                            key={`${item.product_id}-${item.name || item.product_name || ''}`}
                            item={item}
                            canSell={canSell}
                            busy={updatingItems.has(busyKey) || deletingItems.has(busyKey)}
                            onMinus={() => handleDecrementItem(item.product_id, item.name)}
                            onPlus={() => handleIncrementItem(item.product_id, item.name)}
                            onOptions={() => setItemMenu(item)}
                        />
                    );
                })}
                <KitCard sx={{ mt: '12px' }}>
                    <KitKV totals label="Subtotal" value={`R$ ${saleState.subtotal}`} />
                    {hasMoney(totalDiscount) && <KitKV totals label="Desconto" value={`- R$ ${totalDiscount}`} />}
                    <KitKV totals big label="Total" value={`R$ ${totalShown}`} />
                </KitCard>
            </MobileSheet>

            {/* Opções do item (descontos, edição, remoção) */}
            <MobileSheet open={Boolean(itemMenu)} onClose={() => setItemMenu(null)} title={itemMenu ? itemMenu.product_name || itemMenu.name || 'Item' : ''}>
                <Box>
                    {canApplyItemDiscount && (
                        <ActionRow
                            icon={TagIcon}
                            tone="amber"
                            title={itemMenu && itemMenu.item_discount_type ? 'Alterar desconto' : 'Aplicar desconto'}
                            sub={itemMenu && itemMenu.item_discount_type ? `Desconto atual: ${itemMenu.item_discount_type}` : undefined}
                            onClick={() => {
                                const item = itemMenu;
                                setItemMenu(null);
                                openItemDiscountDialog(item);
                            }}
                        />
                    )}
                    {canSell && (
                        <ActionRow
                            icon={EditIcon}
                            title="Editar"
                            onClick={() => {
                                const item = itemMenu;
                                setItemMenu(null);
                                handleEditItem(item);
                            }}
                        />
                    )}
                    {canSell && (
                        <ActionRow
                            icon={TrashIcon}
                            tone="red"
                            title="Remover do carrinho"
                            onClick={() => {
                                const item = itemMenu;
                                setItemMenu(null);
                                handleDeleteItem(item.product_id, item.name);
                            }}
                        />
                    )}
                </Box>
            </MobileSheet>

            {/* Folha "Cliente" (#cliente) */}
            <MobileSheet
                open={customerSheetOpen}
                onClose={() => setCustomerSheetOpen(false)}
                title="Cliente"
                footer={canCreateCustomer ? (
                    <KitButton
                        variant="soft"
                        sx={{ flex: 1 }}
                        onClick={() => {
                            setCustomerSheetOpen(false);
                            setOpenDialogNewCustomer(true);
                        }}
                    >
                        <PlusIcon size={18} /> Novo cliente
                    </KitButton>
                ) : undefined}
            >
                <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <KitSearch
                        value={customerQuery}
                        onChange={(e) => setCustomerQuery(e.target.value)}
                        placeholder="Buscar cliente…"
                        inputProps={{ 'aria-label': 'Buscar cliente', autoComplete: 'off' }}
                    />
                </Box>
                <KitList sx={{ mt: '12px' }}>
                    <AvatarItem text="CF" title="Consumidor final" sub="Sem cadastro" onClick={() => chooseCustomer(null)} />
                    {customerRows.map((c, index) => (
                        <AvatarItem
                            key={c.id}
                            text={initialsOf(c.name)}
                            tone={index % 2 === 0 ? 'soft' : 'purple'}
                            title={c.name}
                            sub={c.sub}
                            onClick={() => chooseCustomer(c)}
                        />
                    ))}
                    {customerRows.length === 0 && customerQuery && (
                        <KitItem title="Nenhum cliente encontrado" />
                    )}
                </KitList>
            </MobileSheet>

            {/* Opções da venda (menu ⋮) */}
            <MobileSheet open={optionsOpen} onClose={() => setOptionsOpen(false)} title="Opções" actionList>
                <Box>
                    {cart.length > 0 && printReceiptEnabled && (
                        <ActionRow
                            icon={PrintIcon}
                            title="Imprimir"
                            onClick={() => {
                                setOptionsOpen(false);
                                handlePrint();
                            }}
                        />
                    )}
                    {cart.length > 0 && canSaleDiscount && !discountApplied && (
                        <ActionRow
                            icon={PercentIcon}
                            tone="amber"
                            title="Aplicar desconto"
                            onClick={() => {
                                setOptionsOpen(false);
                                setDiscountValue('');
                                setDiscountDialogOpen(true);
                            }}
                        />
                    )}
                    {cart.length > 0 && discountApplied && (
                        <ActionRow
                            icon={XIcon}
                            tone="red"
                            title="Remover desconto"
                            onClick={() => {
                                setOptionsOpen(false);
                                handleRemoveSaleDiscount();
                            }}
                        />
                    )}
                    <ActionRow
                        icon={WalletIcon}
                        tone="purple"
                        title="Caixa"
                        sub={saleState.cash_register_name || 'Não selecionado'}
                        onClick={() => {
                            setOptionsOpen(false);
                            setCashDialogOpen(true);
                        }}
                    />
                    <ActionRow
                        icon={colorMode === 'light' ? MoonIcon : SunIcon}
                        tone="gray"
                        title={colorMode === 'light' ? 'Tema escuro' : 'Tema claro'}
                        onClick={() => {
                            setOptionsOpen(false);
                            toggleColorMode();
                        }}
                    />
                    {cart.length > 0 && canCancel && (
                        <ActionRow
                            icon={BanIcon}
                            tone="red"
                            title="Cancelar venda"
                            sub="Exige motivo"
                            onClick={() => {
                                setOptionsOpen(false);
                                setCancelReasonError(false);
                                setOpenDialogCancelSale(true);
                            }}
                        />
                    )}
                </Box>
            </MobileSheet>

            {/* Aplicar desconto na venda */}
            <MobileSheet
                open={discountDialogOpen}
                onClose={() => setDiscountDialogOpen(false)}
                title="Aplicar desconto"
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={() => setDiscountDialogOpen(false)}>Cancelar</KitButton>
                        <KitButton
                            variant="primary"
                            sx={{ flex: 1 }}
                            disabled={!discountValid}
                            onClick={() => {
                                setDiscountDialogOpen(false);
                                handleApplyDiscount(discountValue);
                            }}
                        >
                            Aplicar
                        </KitButton>
                    </>
                )}
            >
                <PosDiscountField label="Desconto" isRequired onChange={(value) => setDiscountValue(value)} />
            </MobileSheet>

            {/* Desconto no produto */}
            <MobileSheet
                open={itemDiscountDialog.open}
                onClose={() => {
                    if (!itemDiscountDialog.loading) setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
                }}
                title="Desconto no produto"
                footer={(
                    <>
                        {itemDiscountCurrent && (
                            <KitButton sx={{ flex: 1 }} variant="danger" disabled={itemDiscountDialog.loading} onClick={() => handleSubmitItemDiscount(true)}>
                                Remover
                            </KitButton>
                        )}
                        <KitButton
                            sx={{ flex: 1 }}
                            disabled={itemDiscountDialog.loading}
                            onClick={() => setItemDiscountDialog({ open: false, item: null, value: '', loading: false })}
                        >
                            Cancelar
                        </KitButton>
                        <KitButton
                            variant="primary"
                            sx={{ flex: 1 }}
                            disabled={!itemDiscountValid || itemDiscountDialog.loading}
                            onClick={() => handleSubmitItemDiscount(false)}
                        >
                            {itemDiscountDialog.loading ? 'Aplicando...' : 'Aplicar'}
                        </KitButton>
                    </>
                )}
            >
                <Box sx={{ fontWeight: 700, lineHeight: 1.4 }}>{itemDiscountDialog.item?.product_name || itemDiscountDialog.item?.name || ''}</Box>
                {itemDiscountCurrent && (
                    <Box sx={{ fontSize: 12.5, color: WARN_LIGHT, fontWeight: 700, lineHeight: 1.4 }}>Desconto atual: {itemDiscountCurrent}</Box>
                )}
                <Box sx={{ mt: '14px' }}>
                    <PosDiscountField
                        label="Desconto do item"
                        onChange={(value) => setItemDiscountDialog((prev) => ({ ...prev, value }))}
                    />
                </Box>
            </MobileSheet>

            {/* Editar produto */}
            <MobileSheet
                open={openEditDialog}
                onClose={handleCancelEdit}
                title={`Editar ${editingItem?.product_name || editingItem?.name || ''}`}
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} disabled={loading} onClick={handleCancelEdit}>Cancelar</KitButton>
                        <KitButton variant="primary" sx={{ flex: 1 }} disabled={loading} onClick={handleSaveEditMobile}>{loading ? 'Salvando...' : 'Salvar'}</KitButton>
                    </>
                )}
            >
                <KitField label="Quantidade">
                    <KitInput
                        value={qtyText}
                        inputMode="numeric"
                        aria-label="Quantidade"
                        onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '');
                            setQtyText(v);
                            const n = parseInt(v, 10);
                            if (n > 0) setEditFormData((prev) => ({ ...prev, quantity: n }));
                        }}
                    />
                </KitField>
                {editingItem?.is_generic && (
                    <KitField label="Nome do produto">
                        <KitInput
                            value={editFormData.name || ''}
                            aria-label="Nome do produto"
                            onChange={(e) => setEditFormData((prev) => ({ ...prev, name: e.target.value }))}
                        />
                    </KitField>
                )}
                {editingItem?.is_generic && (
                    <KitField label="Preço">
                        <KitInput
                            value={editFormData.value}
                            inputMode="numeric"
                            aria-label="Preço"
                            onChange={(e) => setEditFormData((prev) => ({ ...prev, value: formatCurrency(String(e.target.value)) }))}
                        />
                    </KitField>
                )}
                <KitBanner tone={editingItem?.is_generic ? 'info' : 'success'} icon={editingItem?.is_generic ? EditIcon : PackageIcon}>
                    {editingItem?.is_generic
                        ? 'Produto genérico - pode alterar nome, preço e quantidade'
                        : 'Produto cadastrado - pode alterar apenas quantidade'}
                </KitBanner>
            </MobileSheet>

            {/* Cancelar venda */}
            <MobileSheet
                open={openDialogCancelSale}
                onClose={() => {
                    if (!loading) setOpenDialogCancelSale(false);
                }}
                title="Cancelar venda"
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} disabled={loading} onClick={() => setOpenDialogCancelSale(false)}>Não</KitButton>
                        <KitButton sx={{ flex: 1, ...solidDangerSx }} disabled={loading} onClick={handleCancelConfirm}>{loading ? 'Cancelando...' : 'Sim, cancelar'}</KitButton>
                    </>
                )}
            >
                <Box sx={{ fontSize: 13.5, color: 'text.secondary', mb: '14px', lineHeight: 1.4 }}>Tem certeza que deseja cancelar esta venda?</Box>
                <KitField label="Motivo do cancelamento" required help={cancelReasonError ? 'Motivo é obrigatório' : undefined} error={cancelReasonError}>
                    <KitTextarea
                        placeholder="Descreva o motivo…"
                        value={saleState.reason || ''}
                        aria-label="Motivo do cancelamento"
                        sx={cancelReasonError ? { borderColor: 'error.main' } : undefined}
                        onChange={(e) => {
                            setCancelReasonError(false);
                            setSaleState((prev) => ({ ...prev, reason: e.target.value }));
                        }}
                    />
                </KitField>
            </MobileSheet>

            {/* Selecionar caixa */}
            <MobileSheet open={cashDialogOpen} onClose={() => setCashDialogOpen(false)} title="Selecionar caixa">
                {(uiState.cash_register_list || []).length === 0 && (
                    <Box sx={{ color: 'text.disabled', fontSize: 13, lineHeight: 1.4 }}>Nenhum caixa disponível.</Box>
                )}
                <KitList>
                    {(uiState.cash_register_list || []).map((item) => {
                        const selected = item.id === saleState.cash_register_id;
                        return (
                            <KitItem
                                key={item.id}
                                icon={WalletIcon}
                                tone={selected ? undefined : 'gray'}
                                title={item.name}
                                end={selected ? <Box component="span" sx={{ display: 'inline-flex', color: 'primary.main' }}><CheckIcon size={20} /></Box> : undefined}
                                onClick={() => {
                                    handleSelectCashRegister(item.id)
                                        .then(() => {
                                            setSaleState((prev) => ({ ...prev, cash_register_name: item.name, cash_register_id: item.id }));
                                            setCashDialogOpen(false);
                                        })
                                        .catch(() => toast('Erro ao selecionar o caixa', 'error'));
                                }}
                            />
                        );
                    })}
                </KitList>
            </MobileSheet>

            {completedSheet}

            {errorDialog(uiState.showAttention, uiState.attentionMessage, closeAttention)}
            {errorDialog(Boolean(saleState.hasError), saleState.errorMessage, () => setSaleHasError(false))}

            {scannerOpen && (
                <BarcodeScanner
                    onClose={() => setScannerOpen(false)}
                    onError={(message) => toast(message, 'error')}
                    onDetected={(code) => {
                        setScannerOpen(false);
                        handleSearchProduct(code);
                    }}
                />
            )}

            {alertNode}
        </ScreenShell>
    );
}
